/** @jsxRuntime classic */
/** @jsxFrag React.Fragment */
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { formatMoney, identity } from '@bonitour/common-functions'
import { Button, CircleThumbnail, GhostButton, TableRow, TableV2, colors } from '@bonitour/components'
import { formatISOTime } from 'utils/time'
import { ticketStatusDictionary } from 'constants/ticketStatus'
import { circleColor, positionRelative, statusTextColor } from 'assets/styles/global'
import { useCompany } from 'contexts/Company'
import { isDateInFuture } from 'utils/date'
import { ReservationTicketListModal } from './ReservationTicketListModal'
import { useCallback, useMemo, useState } from 'react'
import { ReservationProvider } from 'hooks/context/Reservation'
import { Integrations } from 'constants/integrations'
import { paymentPendingText, paymentPendingThumbnail } from 'domains/MyDay/Table/MyDayTable'

export const ticketMobileBreakpoint = 1270

const actionsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin: -0.375rem 0;

  @media (max-width: ${ticketMobileBreakpoint}px) and (min-width: 450px) {
    flex-direction: row;
  }

  button {
    padding: 6px 8px;
    width: 6.25rem;
    border: 1px solid ${colors.primary};
    &[class*="ghost"] {
      color: ${colors.primary};
    }
  }
`

const lineClamp = (lines = 2) => `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: initial;
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  line-height: 1.1;
  padding: 0.125rem 0;
  word-wrap: break-word;

  span&:not(:last-of-type) {
    margin-bottom: -0.1em;
  }

  .tinier {
    font-size: 0.625rem;
  }

  .smaller {
    font-size: 0.75rem;
  }
`

const fontTiny = css`
  font-size: 0.75rem;
`
const fontSmall = css`
  font-size: 0.8125rem;
`

const tableWrapperStyle = css`
  strong {
    font-weight: 600;
    opacity: 0.9;
  }
`

const labels = [
  'Cód. do Ingresso',
  'Cliente',
  'Data e hora',
  'Tipo',
  'Serviço',
  'Valor',
  'Status',
  'Ação'
]
const columnsWidths = ['9.875rem', '9fr', '6.5rem', '7rem', '16fr', '6.75rem', '9rem', '7.25rem']

/**
 * @param {JSX.Element | JSX.Element[]} children
 * @param {'default' | 'small' | 'tiny'} [fontSize]
 * @param {number} [maxLines]
 * @returns {JSX.Element}
 */
export const TicketTableCell = ({ children, fontSize = 'default', maxLines = 2, ...other }) => (
  <div css={[fontSize === 'tiny' ? fontTiny : fontSize === 'small' ? fontSmall : null, positionRelative]} {...other}>
    {Array.isArray(children)
      ? children.map(
        (child, index) => child
          ? <span key={`ticketcell-line-${index}`} css={[lineClamp(maxLines)]}>{child}</span>
          : null
      )
      : <span css={[lineClamp(maxLines)]}>{children}</span>
    }
  </div>
)

export const TicketStatusTableCell = ({ status }) => {
  const {
    label,
    color,
    textColor
  } = ticketStatusDictionary[status] || {}
  return (
    <TicketTableCell>
      <>
        <CircleThumbnail size={10} css={circleColor(color)} />
        <span css={statusTextColor(textColor)}>{label}</span>
      </>
    </TicketTableCell>
  )
}

export const TicketActionsTableCell = ({ children }) => (
  <div css={actionsContainer}>
    {children}
  </div>
)

export const TicketCheckinListRow = ({ ticket = {}, onValidate = identity, onReservationClick = identity, insideModal = false }) => {
  const { id: companyId } = useCompany()
  const {
    id,
    code,
    client,
    day,
    hour,
    companyId: ticketCompanyId,
    company,
    activity,
    type,
    price,
    serviceType,
    reservationId,
    externalIntegrations,
    paymentStatus
  } = ticket
  const {
    isValidatable = false,
    isValidatableByServiceType: validatableTypes
  } = ticketStatusDictionary[ticket.state] || {}
  const isOwner = ticketCompanyId => ticketCompanyId === companyId
  const canValidateTicket = (companyId, date) =>
    (isValidatable || (Array.isArray(validatableTypes) && validatableTypes.includes(serviceType))) &&
    isOwner(companyId) &&
    !isDateInFuture(date)

  const limberIntegration = useMemo(
    () => externalIntegrations.find(({ integration_name: name }) => name === Integrations.Limber),
    [externalIntegrations]
  )

  const externalVoucherId = useMemo(
    () => externalIntegrations.map(({ external_ticket_id: id } = {}) => id).find(Boolean),
    [externalIntegrations]
  )

  const [limberQR, pickup] = useMemo(
    () => [
      limberIntegration?.limber_qr_code,
      limberIntegration?.extra_data?.nome_local_embarque
    ],
    [limberIntegration]
  )

  const hasPaymentPending = useMemo(() => [ticketStatusDictionary.not_paid.value, ticketStatusDictionary.partially_paid.value].includes(paymentStatus), [paymentStatus])

  return (
    <TableRow key={id}>
      {[
        <TicketTableCell fontSize='tiny' key='code'>
          {hasPaymentPending
            ? (
              <>
                <CircleThumbnail size={8} css={paymentPendingThumbnail}/>
                <span css={paymentPendingText}>Pagamento pendente</span>
              </>
            )
            : null
          }
          {code}
          {externalVoucherId
            ? (
              <>
                <span className='tinier'>Voucher: </span>
                {externalVoucherId}
              </>
            )
            : null}
          {limberQR
            ? (
              <>
                <span className='tinier'>QR: </span>
                {limberQR}
              </>
            )
            : null}
        </TicketTableCell>,
        <TicketTableCell fontSize='small' maxLines={3} key='client'>
          {client}
        </TicketTableCell>,
        <TicketTableCell maxLines={3} key='slot'>
          {formatISOTime(day, 'DD/MM/YYYY', false)}
          {hour}
        </TicketTableCell>,
        <TicketTableCell maxLines={4} key='type'>
          {type}
        </TicketTableCell>,
        <TicketTableCell maxLines={2} key='company+service'>
          {company}
          <strong>{activity}</strong>
          {pickup
            ? (
              <>
                <span className='smaller'>Embarque: </span>
                {pickup}
              </>
            )
            : null}
        </TicketTableCell>,
        <TicketTableCell maxLines={2} key='price'>
          {formatMoney(price)}
        </TicketTableCell>,
        <TicketStatusTableCell status={ticket.state} key='status' />,
        <TicketActionsTableCell key='actions'>
          {!insideModal && <GhostButton onClick={onReservationClick(reservationId)}>Ver reserva</GhostButton>}
          {canValidateTicket(ticketCompanyId, day) && <Button onClick={() => onValidate(ticket)}>Utilizar</Button>}
        </TicketActionsTableCell>
      ]}
    </TableRow>)
}

export const TicketCheckinList = ({ ticketList = [], onValidate = identity, loading, insideModal = false }) => {
  const [openReservationModal, setOpenReservationModal] = useState(null)
  const onReservationClick = useCallback((reservationId) => () => {
    setOpenReservationModal(reservationId)
  }, [])
  const onCloseReservationModal = useCallback(() => {
    setOpenReservationModal(null)
  }, [])
  return (
    <>
      {openReservationModal && !insideModal && (
        <ReservationProvider reservationId={openReservationModal}>
          <ReservationTicketListModal reservationId={openReservationModal} onClose={onCloseReservationModal} />
        </ReservationProvider>
      )}
      <TableV2
        isLoading={loading}
        labels={labels}
        columnsWidths={columnsWidths}
        emptyMessage={'Nenhum ingresso encontrado'}
        customCss={[tableWrapperStyle]}
        mobileBreakpoint={ticketMobileBreakpoint}
      >
        {ticketList?.map((ticket) => (
          <TicketCheckinListRow key={ticket.id} ticket={ticket} onValidate={onValidate} onReservationClick={onReservationClick} insideModal={insideModal} />
        ))}
      </TableV2>
    </>
  )
}
